/**
 * Widget settings integration with the Properties system.
 *
 * Registers user-level properties for the floating render-view widget:
 * visibility, layout (x/y/width/height), and the layered settings cells
 * (pipeline, view mode, attribute, useEDL, etc. — base + override per cell).
 *
 * Layout values are FLOAT properties; they're written during drag and
 * cloud-synced by the property system (which already debounces to 500ms).
 *
 * NOT cloud-synced: camera pose. Pose updates 60×/sec while orbiting and
 * lives on the WidgetOrbitController's local View state. Promote to a
 * property here later if a "save current pose as base" feature is added.
 */

import { propertyRegistry, PropertyType, PropertyValueType } from '../index';
import { ReactiveProperty } from '../reactive/ReactiveProperty';
import { PropertyValueData } from '../types';
import { getGlobalPropertyService } from '../reactive/globalPropertyService';

// Single widget for now; objectIdentifier is empty. When supporting multiple
// widgets, switch this to the widget's id.
const WIDGET_OBJ_ID = '';

// ============================================================================
// VISIBILITY
// ============================================================================

propertyRegistry.register({
  type: PropertyType.USER_SETTINGS,
  identifier: 'render_view_widget_visible',
  name: 'Render View Widget Visible',
  valueType: PropertyValueType.BOOLEAN,
  defaultValue: false,
  description: 'Whether the floating render-view widget is currently shown',
  ui: { group: 'Widget' },
});

export function getWidgetVisibleProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('render_view_widget_visible', WIDGET_OBJ_ID, PropertyType.USER_SETTINGS);
}

// ============================================================================
// LAYOUT (x, y, width, height)
// ============================================================================

const LAYOUT_FIELDS: Array<{ id: string; name: string; defaultValue: number }> = [
  { id: 'render_view_widget_x', name: 'Widget X', defaultValue: 24 },
  { id: 'render_view_widget_y', name: 'Widget Y', defaultValue: 24 },
  { id: 'render_view_widget_width', name: 'Widget Width', defaultValue: 360 },
  { id: 'render_view_widget_height', name: 'Widget Height', defaultValue: 280 },
];

for (const f of LAYOUT_FIELDS) {
  propertyRegistry.register({
    type: PropertyType.USER_SETTINGS,
    identifier: f.id,
    name: f.name,
    valueType: PropertyValueType.FLOAT,
    defaultValue: f.defaultValue,
    description: 'Widget layout position/size in pixels',
    ui: { group: 'Widget' },
  });
}

export function getWidgetLayoutXProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('render_view_widget_x', WIDGET_OBJ_ID, PropertyType.USER_SETTINGS);
}
export function getWidgetLayoutYProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('render_view_widget_y', WIDGET_OBJ_ID, PropertyType.USER_SETTINGS);
}
export function getWidgetLayoutWidthProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('render_view_widget_width', WIDGET_OBJ_ID, PropertyType.USER_SETTINGS);
}
export function getWidgetLayoutHeightProperty(): ReactiveProperty<PropertyValueData> {
  return getGlobalPropertyService().getProperty('render_view_widget_height', WIDGET_OBJ_ID, PropertyType.USER_SETTINGS);
}
